import React from "react";
import { Button as MuiButton, Typography } from "@mui/material";
import Paper from "@components/Paper/Paper";
import PaperItem from "@components/Paper/PaperItem";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import type { MovementServiceSectionField } from "../../types";
import type { MovementServiceDetailsSectionProps } from "./MovementServiceDetailsSection.types";

const TWO_COLUMN_SECTION_IDS = new Set(["movement-info", "times", "additional-info"]);

const LABEL_CLASS = "font-normal text-[#646A77]";
const VALUE_CLASS = "font-semibold text-[#111827]";
const TITLE_SX = { fontWeight: 600, color: "#111827" };
const LANG_BASE = "vessel-call.movement-services.preview.fields";

const SECTION_FIELD_ORDER: Record<string, string[]> = {
  "movement-info": [
    `${LANG_BASE}.from`,
    `${LANG_BASE}.to`,
    `${LANG_BASE}.from-area-berth`,
    `${LANG_BASE}.from-berth`,
    `${LANG_BASE}.from-location`,
    `${LANG_BASE}.to-area-berth`,
    `${LANG_BASE}.to-berth`,
    `${LANG_BASE}.terminal`,
    `${LANG_BASE}.latitude`,
    `${LANG_BASE}.longitude`,
    `${LANG_BASE}.latitude-longitude`,
  ],
  "times": [
    `${LANG_BASE}.requested-time-service`,
    `${LANG_BASE}.vessel-readiness-etd`,
    `${LANG_BASE}.permission-to-sail-time`,
    `${LANG_BASE}.anchor-aweigh-time-atc`,
    `${LANG_BASE}.rts`,
    `${LANG_BASE}.rta`,
    `${LANG_BASE}.rtd`,
    `${LANG_BASE}.eta`,
    `${LANG_BASE}.etd`,
    `${LANG_BASE}.ets`,
    `${LANG_BASE}.ats`,
    `${LANG_BASE}.etc`,
    `${LANG_BASE}.pilot-onboard`,
    `${LANG_BASE}.all-fast`,
    `${LANG_BASE}.all-lines-clear`,
    `${LANG_BASE}.all-clear`,
    `${LANG_BASE}.atc`,
    `${LANG_BASE}.pilot-underway`,
    `${LANG_BASE}.pilot-away`,
    `${LANG_BASE}.anchor-time`,
    `${LANG_BASE}.anchor-aweigh-time`,
    `${LANG_BASE}.vessel-anchored-shackles`,
  ],
  "additional-info": [
    `${LANG_BASE}.vessel-condition`,
    `${LANG_BASE}.movement-reason`,
    `${LANG_BASE}.draft-fore`,
    `${LANG_BASE}.draft-aft`,
    `${LANG_BASE}.pilotage-type`,
    `${LANG_BASE}.specify-if-others`,
    `${LANG_BASE}.protrusions`,
    `${LANG_BASE}.oil-stains`,
    `${LANG_BASE}.comments`,
  ],
};

type SectionFieldWithMeta = MovementServiceSectionField & {
  isTitle?: boolean;
};

const getFieldTestId = (field: MovementServiceSectionField): string =>
  field.labelKey.replace(`${LANG_BASE}.`, "").replace(/\s+/g, "-").toLowerCase();

const MovementServiceDetailsSection: React.FC<MovementServiceDetailsSectionProps> = ({
  section,
  testId,
  movementDetails,
  hideTitle = false,
  showActions = false,
  onEdit,
  onRemove,
}) => {
  const { t } = useTranslation();
  const { id: vesselCallId } = useParams<{ id: string }>();
  const userActionPermissions = useAuthStore((state) => state.userActionPermissions);
  const user = useAuthStore((state) => state.user);

  const isTerminalUserWithAccess =
    user?.terminalId &&
    (user?.terminalId === movementDetails?.to || user?.terminalId === movementDetails?.from);

  const canViewPortServiceLink =
    userActionPermissions.includes("canSeeHyperlinkAssociatedPortService") ||
    isTerminalUserWithAccess;

  const hiddenLabels = new Set([`${LANG_BASE}.terminal`]);
  const filteredFields = section.fields
    .filter((field) => !hiddenLabels.has(field.labelKey))
    .map((field) => ({ ...field }));

  if (!filteredFields.length) {
    return null;
  }

  const title = t(section.titleKey, section.titleParams);

  const showInnerTitle = !hideTitle && Boolean(title);
  const fieldsWithMeta: SectionFieldWithMeta[] = filteredFields;

  const nonTitleFieldCount = filteredFields.length;

  const useColumnLayout = TWO_COLUMN_SECTION_IDS.has(section.id) && nonTitleFieldCount > 1;

  const orderedFields = (() => {
    if (section.preserveFieldOrder) {
      return fieldsWithMeta;
    }

    const order = SECTION_FIELD_ORDER[section.id];

    if (!order) {
      return fieldsWithMeta;
    }

    const orderMap = new Map(order.map((label, idx) => [label, idx]));

    return fieldsWithMeta
      .map((field, index) => ({
        field,
        index,
        order: field.isTitle ? -1 : (orderMap.get(field.labelKey) ?? Number.MAX_SAFE_INTEGER),
      }))
      .sort((a, b) => {
        if (a.order !== b.order) {
          return a.order - b.order;
        }

        return a.index - b.index;
      })
      .map(({ field }) => field);
  })();

  const buildRows = () => {
    const rows: SectionFieldWithMeta[][] = [];

    for (let i = 0; i < orderedFields.length; ) {
      const field = orderedFields[i];

      if (field.isTitle) {
        rows.push([field]);
        i += 1;
        continue;
      }

      if (field.isFullRow) {
        rows.push([field]);
        i += 1;
        continue;
      }

      if (!useColumnLayout) {
        rows.push([field]);
        i += 1;
        continue;
      }

      const nextField = orderedFields[i + 1];
      if (nextField && !nextField.isTitle && !nextField.isFullRow) {
        rows.push([field, nextField]);
        i += 2;
      } else {
        rows.push([field]);
        i += 1;
      }
    }

    return rows;
  };

  const rows = buildRows();
  const hasTitleRow = rows[0]?.[0]?.isTitle ?? false;

  return (
    <Paper variant="outlined" data-testid={testId}>
      <div className="flex flex-col gap-3">
        {showInnerTitle ? (
          <>
            <div className="flex justify-between items-center">
              <Typography variant="subtitle1" className="font-bold" sx={TITLE_SX}>
                {title}
              </Typography>
              {showActions ? (
                <div className="flex items-center">
                  <MuiButton
                    variant="text"
                    onClick={onEdit}
                    sx={{ textTransform: "none", color: "primary.main" }}
                  >
                    {t("vessel-call.movement-services.buttons.edit")}
                  </MuiButton>
                  <MuiButton
                    variant="text"
                    onClick={onRemove}
                    sx={{ textTransform: "none", color: "primary.main" }}
                  >
                    {t("vessel-call.movement-services.buttons.remove")}
                  </MuiButton>
                </div>
              ) : null}
            </div>
          </>
        ) : null}
        <div className="flex flex-col">
          {rows.map((row, rowIndex) => {
            const isTitleRow = row[0]?.isTitle;

            if (isTitleRow) {
              return (
                <Typography
                  key={`${section.id}-row-${rowIndex}`}
                  variant="subtitle1"
                  className="font-bold pb-3"
                  sx={TITLE_SX}
                  data-testid={`${testId}-${getFieldTestId(row[0])}`}
                >
                  {t(row[0].labelKey)}
                </Typography>
              );
            }

            const effectiveRowIndex = hasTitleRow ? rowIndex - 1 : rowIndex;
            const isFirstRow = effectiveRowIndex === 0;
            const rowClass = useColumnLayout ? "grid gap-4 grid-cols-2" : "grid gap-4 grid-cols-1";

            return (
              <div key={`${section.id}-row-${rowIndex}`} className={rowClass}>
                {row.map((field, columnIndex) =>
                  field.isPlaceholder ? (
                    <div
                      key={`${section.id}-${field.labelKey}-${rowIndex}-${columnIndex}`}
                      className={`mt-3 mb-3 ${isFirstRow ? "pt-3" : "border-t border-[#F1F2F4] pt-3"}`}
                      aria-hidden="true"
                      data-testid={`${testId}-${getFieldTestId(field)}-placeholder`}
                    />
                  ) : (
                    <PaperItem
                      key={`${section.id}-${field.labelKey}-${rowIndex}-${columnIndex}`}
                      translate={t(field.labelKey)}
                      text={field.value ?? "-"}
                      textStyles={VALUE_CLASS}
                      tranStyle={LABEL_CLASS}
                      wrapperStyle={isFirstRow ? "pt-3" : "border-t border-[#F1F2F4] pt-3"}
                      testId={`${testId}-${getFieldTestId(field)}`}
                    />
                  ),
                )}
                {useColumnLayout && row.length === 1 ? <div /> : null}
              </div>
            );
          })}
          {movementDetails?.associatedPortService && (
            <div
              key={movementDetails.associatedPortService.portServiceId}
              className="grid gap-4 grid-cols-2"
            >
              <PaperItem
                translate={t("vessel-call.movement-services.fields.associated-port-service")}
                text={
                  canViewPortServiceLink ? (
                    <Link
                      to={`/vessel-call/vessel-call-status/${vesselCallId}/port-services/${movementDetails.associatedPortService.portServiceId}`}
                      className="text-blue-600 hover:underline"
                    >
                      {`${movementDetails.associatedPortService.serviceRequestNumber} - ${movementDetails.associatedPortService.serviceCategory}`}
                    </Link>
                  ) : (
                    `${movementDetails.associatedPortService.serviceRequestNumber} - ${movementDetails.associatedPortService.serviceCategory}`
                  )
                }
                textStyles={VALUE_CLASS}
                tranStyle={LABEL_CLASS}
                wrapperStyle="border-t border-[#F1F2F4] pt-3"
                testId={`${testId}-associated-port-service-${movementDetails.associatedPortService.portServiceId}`}
              />
              <div />
            </div>
          )}
        </div>
      </div>
    </Paper>
  );
};

export default MovementServiceDetailsSection;
